import { memo, useCallback, useMemo, useState } from 'react';

import { Modal } from '@air/primitive-modal';
import { ShortUrlObjectType } from '@air/types';

import { CreateSharePrivateCollectionModalHeader } from '~/components/SharePrivateCollectionModal/components/CreateSharePrivateCollectionModalHeader';
import { SharePrivateCollectionModalForm } from '~/components/SharePrivateCollectionModal/components/SharePrivateCollectionModalForm';
import {
  DEFAULT_PERMISSIONS,
  useCreateSharePrivateCollection,
} from '~/components/SharePrivateCollectionModal/hooks/useCreateSharePrivateCollection';
import { SharePrivateCollectionModalProvider } from '~/components/SharePrivateCollectionModal/SharePrivateCollectionModalContext';
import { ShareLinkTeamInviteNudgePanel } from '~/components/SharePrivateContentModal/ShareLinkTeamInviteNudgePanel';
import { useShowShareLinkTeamInviteNudgeForModal } from '~/hooks/useShowShareLinkTeamInviteNudgeForModal';
import { useShowSubscriptionExpiredModal } from '~/hooks/useShowSubscriptionExpiredModal';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useGetDefaultSettings } from '~/swr-hooks/shortUrl/useGetDefaultSettings';
import { canCreateCollection } from '~/utils/permissions/workspacePermissions';

export type CreateSharePrivateCollectionModalProps = {
  onClose: () => void;
  assetIds: string[];
  boardIds: string[];
};

export const CreateSharePrivateCollectionModal = memo(
  ({ onClose, assetIds, boardIds }: CreateSharePrivateCollectionModalProps) => {
    const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
    const { currentWorkspace } = useCurrentWorkspace();
    const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({ onClose });
    const [hasCreatedShareLink, setHasCreatedShareLink] = useState(false);
    const { isShareLinkTeamInviteNudgeVisible, openShareLinkTeamInviteNudgeModal, workspaceName } =
      useShowShareLinkTeamInviteNudgeForModal({
        isShareLinkCreated: hasCreatedShareLink,
        isShareLinkReady: hasCreatedShareLink,
        location: 'collection_share_link',
        onShareLinkCreatedFallback: onClose,
      });
    const { isLoading: isLoadingDefaultSettings, data: defaultSettings } = useGetDefaultSettings({
      workspaceId: currentWorkspace?.id ?? '',
      objectType: ShortUrlObjectType.collection,
    });

    const permissions = useMemo(
      () => ({ ...DEFAULT_PERMISSIONS, ...defaultSettings?.permissions }),
      [defaultSettings?.permissions],
    );

    const handleShareLinkCreated = useCallback(() => {
      setHasCreatedShareLink(true);
    }, []);

    const props = useCreateSharePrivateCollection({
      assetIds,
      boardIds,
      onShareLinkCreated: handleShareLinkCreated,
      permissions,
    });

    const canCreate = canCreateCollection(workspacePermissions);

    const contextValue = useMemo(
      () => ({
        ...props,
        isEditMode: false,
        errors: props.validationErrors,
        hasCreatedShareLink,
        onSave: props.handleCreate,
        onClose,
      }),
      [props, hasCreatedShareLink, onClose],
    );

    if (!canCreate || showingSubscriptionExpiredModal || isLoadingDefaultSettings) {
      return null;
    }

    return (
      <Modal
        data-testid="SHARE_PRIVATE_CONTENT_MODAL"
        className="my-auto bg-transparent p-0 focus-visible:ring-0"
        dangerouslyBypassFocusLock
        overlayClassName="flex min-h-full items-center justify-center"
        isOpen
        onDismiss={onClose}
      >
        <div className="rounded-6 bg-surface-1">
          <SharePrivateCollectionModalProvider value={contextValue}>
            <CreateSharePrivateCollectionModalHeader onClose={onClose} />
            <SharePrivateCollectionModalForm />
          </SharePrivateCollectionModalProvider>
        </div>
        {isShareLinkTeamInviteNudgeVisible && (
          <ShareLinkTeamInviteNudgePanel
            onInviteClick={openShareLinkTeamInviteNudgeModal}
            workspaceName={workspaceName}
          />
        )}
      </Modal>
    );
  },
);

CreateSharePrivateCollectionModal.displayName = 'CreateSharePrivateCollectionModal';
